import React, { useEffect, useContext, useReducer } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserEntryCard from '../cardLibrary/UserEntry';
import TerminusCard from '../cardLibrary/Terminus';
import EnrollAxiadid from '../cardLibrary/EnrollAxiadid';
import UpdatePiv from '../cardLibrary/UpdatePiv';
import MessageCard from '../cardLibrary/MessageCard';
import DeviceDetection from '../elementLibrary/DeviceDetection';
import FlowButtons from '../elementLibrary/FlowButtons';
import LoadingSpinner from '../shared/LoadingSpinner';
import { AppDispatchContext, AppStateContext } from '../../context/AppContext';
import { EnrollingDispatchContext } from '../../context/EnrollingContext';
import { PaddingDivider } from '../../styles/common';
import { HelpDeskDispatchContext } from '../../context/HelpDeskContext';
import { paths, KIOSK_MODE } from '../../constants';
import { fetchUpdateWalkway } from '../../utils/walkways';
import ValidateQuestionCard from '../cardLibrary/ValidateQuestion';
import DeviceNotFound from '../cardLibrary/DeviceNotFound';
import EnrollQuestionCard from '../cardLibrary/EnrollQuestion';

const DETECTION = 'detection';
const TERMINUS = 'terminus';

export function reducer(state, action) {
    switch (action.type) {
        case 'SET_UPDATE_WORKFLOW':
            return {
                ...state,
                workflow: action.payload,
                fetching: false,
            };
        case 'SET_WORKFLOW':
            return {
                ...state,
                workflow: action.payload,
            };
        case 'SET_NEXT_STEP':
            return {
                ...state,
                step: state.step + 1,
            };
        case 'SET_BACK_STEP':
            return {
                ...state,
                step: state.step - 1,
            };
        case 'SET_DEVICE':
            return {
                ...state,
                device: action.payload,
            };
        case 'SET_READER':
            return {
                ...state,
                reader: action.payload,
            };
        case 'SET_READER_LIST':
            return {
                ...state,
                readerList: action.payload,
            };
        case 'SET_DETECTED':
            return {
                ...state,
                detected: true,
            };
        case 'SET_CURRENT_ANSWER':
            return {
                ...state,
                challenges: {
                    ...state.challenges,
                    [action.payload.session]: action.payload.answer,
                },
            };
        default:
            return state;
    }
}

const Update = () => {
    const appDispatch = useContext(AppDispatchContext);
    const {
        errorMsg: errorMessage,
        device,
        goBackLink,
        viewMode,
    } = useContext(AppStateContext);
    const enrollingDispatch = useContext(EnrollingDispatchContext);
    const helpDeskDispatch = useContext(HelpDeskDispatchContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [state, dispatch] = useReducer(reducer, {
        workflow: [
            {
                type: DETECTION,
            },
        ],
        fetching: true,
        step: 0,
        device,
        reader: '',
        readerList: {},
        detected: false,
    });

    const location = useLocation();
    const { pathname } = location || {};
    const isHelpDesk = pathname ? pathname.includes(paths.helpDesk) : false;
    const returnPath =
        goBackLink || (isHelpDesk ? paths.helpDesk : paths.identities);
    const isKiosk = viewMode === KIOSK_MODE;
    const user = device ? device.user : undefined;
    const deviceNotFound = () =>
        dispatch({
            type: 'SET_UPDATE_WORKFLOW',
            payload: [{ type: 'deviceNotFound' }],
        });

    const components = {
        /* eslint-disable react/prop-types */
        loadingSpinner: ({ title }, mgmt) => (
            <LoadingSpinner title={title} mgmt={mgmt} flowButtons />
        ),
        userEntry: ({ setCurrentPin, setCurrentAnswer }, mgmt) => (
            <UserEntryCard
                setCurrentPin={setCurrentPin}
                setCurrentAnswer={setCurrentAnswer}
                mgmt={mgmt}
                device={device}
            />
        ),
        questionValidate: (thisProps, mgmt) => (
            <ValidateQuestionCard
                mgmt={mgmt}
                deviceUser={device && device.user}
            />
        ),
        axiadidEnroll: (thisProps, mgmt) => <EnrollAxiadid mgmt={mgmt} />,
        pivUpdate: ({ device, reader }, mgmt) => (
            <UpdatePiv device={device} reader={reader} mgmt={mgmt} readerList={state.readerList} />
        ),
        terminus: ({ title, errorMsg }) => (
            <TerminusCard
                title={title}
                errorMsg={errorMsg}
                msg={t('updateSuccessMsg'.concat(isKiosk ? '$kiosk' : ''))}
                path={returnPath}
            />
        ),
        detection: ({ msg, title, alertStyle, device, setReader, setReaderList }, mgmt) => (
            <>
                <MessageCard
                    msg={msg}
                    title={title}
                    alertStyle={alertStyle}
                    mgmt={mgmt}
                >
                    <DeviceDetection
                        device={device}
                        mgmt={mgmt}
                        appDispatch={appDispatch}
                        enrollingDispatch={enrollingDispatch}
                        setReader={setReader}
                        setReaderList={setReaderList}
                        handleNotFound={deviceNotFound}
                    />
                    <PaddingDivider />
                    <FlowButtons
                        mgmt={{
                            next: mgmt.next,
                            back: mgmt.back,
                            showBack: true,
                            showNext: false,
                        }}
                    />
                </MessageCard>
            </>
        ),
        deviceNotFound: ({ title }) => <DeviceNotFound title={title} />,
        questionEnroll: (thisProps, mgmt) => (
            <EnrollQuestionCard stepId='questionEnroll' mgmt={mgmt} />
        ),
    };

    const setUpdateWorkflow = (walkway) => {
        const isAxiadid = walkway.some((step) => step.type === 'axiadidEnroll');
        dispatch({
            type: 'SET_UPDATE_WORKFLOW',
            payload: [
                ...(isAxiadid ? [] : state.workflow),
                ...walkway,
                {
                    type: TERMINUS,
                },
            ],
        });
        enrollingDispatch({
            type: 'SET_CREDENTIAL_FLOW',
            credentialFlow: walkway,
        });
    };

    useEffect(() => {
        if (device) {
            fetchUpdateWalkway(
                setUpdateWorkflow,
                appDispatch,
                device,
                { user },
            );
        }
        return () => {
            enrollingDispatch({ type: 'RESET_FLOWS' });
            helpDeskDispatch({ type: 'RESET_CONTEXT' });
            appDispatch({ type: 'RESET_FLOWS' });
        };
    },[]);

    useEffect(() => {
        enrollingDispatch({
            type: 'SET_USERNAME',
            username: user,
        });
    }, [user, enrollingDispatch]);

    const homeBack = () => {
        navigate(returnPath);
    };

    const setUserEntryEnrolling = (step) => {
        const { type } = state.workflow[step];
        if (type !== 'userEntry') {
            return;
        }
        enrollingDispatch({
            type: 'SET_USER_ENTRY',
            userEntry: state.workflow[step],
        });
    };

    const next = () => {
        if (state.step < state.workflow.length) {
            setUserEntryEnrolling(state.step + 1);
            dispatch({
                type: 'SET_NEXT_STEP',
            });
        }
    };

    const back = () => {
        if (state.step > 0) {
            setUserEntryEnrolling(state.step - 1);
            dispatch({
                type: 'SET_BACK_STEP',
            });
        }
    };

    if (!device) {
        return <Navigate to={returnPath} replace />;
    }

    return (
        <>
            {state.fetching ? (
                <LoadingSpinner />
            ) : (
                components[state.workflow[state.step].type](
                    {
                        // props
                        errorMsg: errorMessage,
                        title: t('cardTitles$updateDevice'),
                        device: state.device,
                        setCurrentPin: (currentPin) =>
                            dispatch({
                                type: 'SET_DEVICE',
                                payload: {
                                    ...state.device,
                                    pin: currentPin,
                                    user: state.device.user
                                        ? state.device.user
                                        : undefined,
                                },
                            }),
                        setCurrentAnswer: (answer, session) => {
                            dispatch({
                                type: 'SET_CURRENT_ANSWER',
                                payload: {
                                    answer,
                                    session,
                                },
                            });
                        },
                        setReader: (reader) =>
                            dispatch({
                                type: 'SET_READER',
                                payload: reader,
                            }),
                        setReaderList: (readerList) =>
                            dispatch({
                                type: 'SET_READER_LIST',
                                payload: readerList,
                            }),
                        reader: state.reader,
                        readerList: state.readerList,
                    },
                    {
                        // state
                        workflow: state.workflow,
                        fetching: state.fetching,
                        step: state.step,
                        device: state.device,
                        reader: state.reader,
                        detected: state.detected,
                        answer: state.answer,
                        session: state.session,
                        challenges: state.challenges,
                        next,
                        showNext: false,
                        showBack: state.step > 0,
                        back: state.step > 0 ? back : homeBack,
                        setWorkflow: setUpdateWorkflow,
                        setDetected: async () =>
                            dispatch({
                                type: 'SET_DETECTED',
                            }),
                        homeBack,
                    },
                )
            )}
        </>
    );
};

export default Update;
